import * as React from "react";
import axios from "axios";
import { Link2, Trash2, AlertTriangle, Loader2 } from "lucide-react";

import {
  Card,
  CardHeader,
  CardContent,
  CardDescription,
  CardTitle,
} from "./card";
import { Button } from "./button";
import { Text } from "./typography";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./alert-dialog";
import { API_BASE_URL } from "../../lib/api";

/**
 * ConnectedAppsSection — Settings "Connected apps" (issue #257, Issue 3 of
 * .claude/docs/tasks/mcp-oauth/context.md).
 *
 * Lists this user's active OAuth grants (third-party clients approved on the
 * consent page — see `../../pages/OAuthConsentPage.jsx`) and lets them be
 * revoked. Revoking deletes the grant server-side, which immediately
 * invalidates its access/refresh tokens (the next MCP call with that token
 * fails). Confirmation is in-page (AlertDialog), never `window.confirm`,
 * matching McpApiKeySection's pattern for the same kind of destructive action.
 */
const SCOPE_LABELS = {
  "portfolio:read": "Read",
  "portfolio:write": "Write",
};

function formatScope(scope) {
  if (!scope) return "";
  return scope
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => SCOPE_LABELS[s] || s)
    .join(" · ");
}

function formatDate(dateStr) {
  if (!dateStr) return "Never";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ConnectedAppsSection() {
  const [grants, setGrants] = React.useState(null); // null = loading
  const [error, setError] = React.useState(null);
  const [revokeTarget, setRevokeTarget] = React.useState(null); // grant being confirmed
  const [revoking, setRevoking] = React.useState(false);
  // Shown inside the dialog: the card-level error sits behind the modal overlay.
  const [revokeError, setRevokeError] = React.useState(null);

  const fetchGrants = React.useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/oauth/grants`, {
        withCredentials: true,
      });
      setGrants(response.data.grants || []);
    } catch (err) {
      setError(
        err.response?.data?.error || err.response?.data?.message || err.message
      );
      setGrants([]);
    }
  }, []);

  React.useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setRevoking(true);
      setRevokeError(null);
      await axios.delete(`${API_BASE_URL}/oauth/grants/${revokeTarget.grantId}`, {
        withCredentials: true,
      });
      setGrants((prev) => (prev || []).filter((g) => g.grantId !== revokeTarget.grantId));
      setRevokeTarget(null);
    } catch (err) {
      setRevokeError(
        err.response?.data?.error || err.response?.data?.message || err.message
      );
    } finally {
      setRevoking(false);
    }
  };

  const loading = grants === null;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Connected apps</CardTitle>
          </div>
          <CardDescription>
            Third-party apps you&apos;ve approved to access your portfolio data
            via OAuth (e.g. ChatGPT). Revoking access here signs the app out
            immediately.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && grants.length === 0 && (
            <div className="rounded-lg border border-dashed border-border p-6 text-center">
              <Link2 className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
              <Text variant="secondary">
                No apps connected yet. Apps you approve on the OAuth consent
                page will show up here.
              </Text>
            </div>
          )}

          {!loading && grants.length > 0 && (
            <ul className="space-y-3">
              {grants.map((grant) => (
                <li
                  key={grant.grantId}
                  className="rounded-lg border border-border p-4 space-y-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {/* Plain text node — clientName is client-declared
                          (DCR), never rendered as HTML. */}
                      <Text className="font-medium truncate">{grant.clientName}</Text>
                      {grant.redirectHost && (
                        <Text variant="secondary" size="sm" className="font-mono break-all">
                          {grant.redirectHost}
                        </Text>
                      )}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setRevokeTarget(grant)}
                      className="shrink-0"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Text variant="secondary" size="sm">
                        Access
                      </Text>
                      <Text size="sm">{formatScope(grant.scope) || "—"}</Text>
                    </div>
                    <div>
                      <Text variant="secondary" size="sm">
                        Last used
                      </Text>
                      <Text size="sm">{formatDate(grant.lastUsedAt)}</Text>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRevokeTarget(null);
            setRevokeError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle>Revoke access</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              {revokeTarget && (
                <>
                  This will immediately disconnect{" "}
                  <strong>{revokeTarget.clientName}</strong> from your
                  portfolio data. It can request access again later, which
                  you would need to approve again.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {revokeError && (
            <div role="alert" className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                Could not revoke access: {revokeError}
              </Text>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleRevoke} disabled={revoking}>
              {revoking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                "Revoke access"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ConnectedAppsSection;
